import { Icon } from "@blueprintjs/core";
import * as monaco from "monaco-editor-core";
import { IDisposable } from "monaco-editor-core";

import { Vec2 } from "../../Data/Geometry/Vec2";
import { Panic } from "../../Errors/Panic";
import { InfoRequestResult, TypeInfo } from "../../gen/InfoRequest";
import styles from "./InfoPopup.module.scss";
import { createPopup, Popup } from "./Popup";

export class InfoPopup implements IDisposable {
  static getSize(): { width: number; height: number } {
    return {
      width: 320,
      height: 144
    };
  }

  /**
   * @param info A value of `null` means loading
   */
  constructor(position: Vec2, private listener: InfoPopup.Events) {
    this.popup = createPopup(
      {
        animationTransform: { x: -128, y: 0 },
        position: position,
        size: InfoPopup.getSize(),
        titleBarClassName: styles.titlebar,
        renderTitleBar: () => <TitleBar />
      },
      {
        onExpand: this.listener.onExpand
      }
    );

    this.editor = createMonacoEditor(this.popup.content);
    this.editor.editorInstance.focus();
  }

  public setInfoData(infoData: InfoPopup.InfoData): void {
    const renderedTypeInfo = renderTypeInfo(infoData.infoRequestResult.typeInfoSpans);
    this.editor.model.setValue(renderedTypeInfo);
  }

  dispose(): void {
    this.popup.dispose();
  }

  private popup: Popup;
  private editor: Editor;
}

export declare namespace InfoPopup {
  export interface Events {
    onExpand: () => void;
  }

  export interface InfoData {
    infoRequestResult: InfoRequestResult;
  }
}

interface TitleBarProps {}

function TitleBar(props: TitleBarProps): JSX.Element {
  return (
    <>
      <Icon icon="info-sign" className={styles.titlebar_icon} /> Info
    </>
  );
}

interface Editor {
  editorInstance: monaco.editor.IStandaloneCodeEditor;
  model: monaco.editor.ITextModel;
}

function createMonacoEditor(parent: HTMLElement): Editor {
  // "Is there a way to disable IntelliSense?" <https://github.com/microsoft/monaco-editor/issues/1681>
  const editorInstance = monaco.editor.create(parent, {
    readOnly: true,
    lineNumbers: "on",
    lineNumbersMinChars: 2,
    padding: {
      top: 3,
      bottom: 3
    },
    renderIndentGuides: false,
    codeLens: false,
    lightbulb: {
      enabled: false
    },
    glyphMargin: false,
    // lineDecorationsWidth: 0,
    matchBrackets: "never",
    selectionHighlight: false,
    tabSize: 2,
    automaticLayout: true,
    scrollbar: {
      verticalHasArrows: true,
      useShadows: false,
      arrowSize: 15,
      horizontalScrollbarSize: 15,
      verticalScrollbarSize: 15
    },
    detectIndentation: false,
    autoClosingBrackets: "never",
    quickSuggestions: false,
    snippetSuggestions: "none",
    occurrencesHighlight: false,
    contextmenu: false,
    scrollBeyondLastLine: false,
    autoIndent: "keep",
    suggest: {
      showStatusBar: false,
      showSnippets: false
    },
    dragAndDrop: false,
    suggestOnTriggerCharacters: false,
    minimap: {
      enabled: false
    },
    folding: false,
    hideCursorInOverviewRuler: true,
    renderLineHighlight: "none"
  });

  const model = editorInstance.getModel();
  if (model === null) {
    throw new Panic("IStandaloneCodeEditor missing default model", {});
  }

  return {
    editorInstance: editorInstance,
    model: model
  };
}

function renderTypeInfo(typeInfoSpans: TypeInfo[]): string {
  const header = "Expression Type Info:\n";
  const body =
    typeInfoSpans.length > 0
      ? typeInfoSpans.map((typeInfoSpan) => "  " + typeInfoSpan.typeString).join("\n")
      : "  No Type Info";

  return header + body;
}
